import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, input, computed, Signal } from '@angular/core';
import { AppService } from 'app/app.service';
import { YAxes, YAxis, Average, XAxis, Annotation, LegendItem } from './graph.model';
import { KeyGraphBaseComponent } from './base.component';
import * as Color from 'color';
import { merge } from 'lodash';
@Component({
    selector: 'key-graph',
    templateUrl: './graph.component.html',
    styleUrls: ['./base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeyGraphComponent extends KeyGraphBaseComponent implements OnChanges {

    yAxes = input<YAxes>();
    xAxis = input<XAxis>();
    averages = input<Average[]>();

    chartSpecificConfig: Signal<any> = computed(() => {
        return merge(this.baseConfig, {
            type: this.datasets()?.every(x => x.type === 'line') ? 'line' : 'bar',
            options: {
                annotation: {
                    annotations: this.getAnnotations(this.averages()),
                },
                elements: {
                    point: {
                        radius: 0,
                        hitRadius: 10,
                    },
                },
                scales: {
                    x: {
                        type: 'category',
                        display: !(this.xAxis()?.hidden),
                        ticks: {
                            font: {
                                family: this.axesLabelFont,
                                size: this.axesLabelSize,
                            },
                            color: this.axesLabelColor,
                            minRotation: 0,
                            maxRotation: this.xAxis()?.autoLabels ? 90 : 0,
                            autoSkip: true,
                            autoSkipPadding: 15,
                            callback: (value, index) => {
                                const label = this.labels()[index];
                                const displayValue = label?.value || value;
                                return this.xAxis()?.format ? this.xAxis().format(displayValue) : displayValue;
                            },
                        },
                        stacked: this.stacked(),
                        grid: {
                            display: false,
                            drawBorder: false,
                        },
                    },
                    ...this.getYAxes(this.yAxes()),
                },
                plugins: {
                    tooltip: {
                        enabled: this.tooltipMode() !== 'none',
                        callbacks: {
                            title: tooltipItem => {
                                const index = tooltipItem[0].dataIndex;
                                const label = this.labels()[index];
    
                                if (label?.hideTooltipTitle === true) { return null; }
                                return label?.tooltipTitle || label?.value;
                            },
                            label: tooltipItem => {
                                if (isNaN(tooltipItem.parsed.y)) { return null; }
    
                                if (!this.datasets() || !this.datasets()[tooltipItem.datasetIndex]) {
                                    return tooltipItem.parsed.y;
                                }
    
                                const dataset = this.datasets()[tooltipItem.datasetIndex];
                                const label = dataset.label;
                                const dataValue = dataset.data[tooltipItem.dataIndex].value;
    
                                const getValue = (value: string, prefix: string, suffix: string): string => {
                                    if (prefix) {
                                        value = prefix + ' ' + value;
                                    }
                                    if (suffix) {
                                        value += ' ' + suffix;
                                    }
                                    return value;
                                };
    
                                if (!label || !dataValue) { return tooltipItem.yLabel; }
                                return (typeof label === 'string') ? `${label}: ${dataValue}` : `${label.name}: ${getValue(dataValue, label.prefix, label.suffix)}`;
                            },
                        },
                    },
                }
            }
        })
    })

    constructor(public app: AppService) {
        super(app);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
    }

    updateGraphConfig() {
        const fadeColor = color => Color(color).fade(.4).string();

        const data = {
            data: {
                labels: this.labels().map(x => x.value),
                datasets: this.datasets()?.map((x, i) => {
                    const backgroundColor = x.fill ? fadeColor(x.color || this.colors && this.colors[i] || 'rgba(47, 166, 212, 0.6)') : 'rgba(0, 0, 0, 0)';
                    const borderColor = x.color || this.colors && this.colors[i] || 'rgb(47, 166, 212)';
                    return  {
                        ...this.getDataset(x, i),
                        backgroundColor: x.colors ? x.colors : backgroundColor,
                        borderColor: x.borderColors ? x.borderColors : borderColor,
                        type: x.type || 'bar',
                        fill: x.fill ? 'start' : false,
                        lineTension: x.curvedLines ? .3 : 0,
                        steppedLine: x.steppedLines,
                        yAxisID: x.yAxis || 'left',
                        borderDash: x.borderDash || [],
                        borderWidth: x.borderWidth,
                        maxBarThickness: x.maxBarThickness || undefined,
                    }
                }) || [],
            },
        }
        this.config = merge({}, this.chartSpecificConfig(), data);
        this.updateGraph();
    }

    getYAxes(yAxes: YAxes = { left: {} }): any {
        return Object.entries(yAxes)
            .reduce((acc, [position, options]: [string, YAxis]) => {
                acc[position] = {
                    display: !options.hidden,
                    position,
                    type: 'linear',
                    stacked: this.stacked(),
                    ticks: {
                        min: options.min,
                        max: options.max,
                        font: {
                            family: this.axesLabelFont,
                            size: this.axesLabelSize,
                        },
                        color: this.axesLabelColor,
                        callback: (value: any) => {
                            return options.labelFn ? options.labelFn(value) : this.abbreviateNumber(value);
                        },
                    },
                };
                return acc;
            }, {});
    }

    getAnnotations(items: Average[] = []): Annotation[] {
        return items.map(item => {
            const value = item['selectedMeasureFormat'] === 'duration' ? (Number(item.value) / 3600) : item.value;

            return {
                type: 'line',
                mode: item.mode || 'horizontal',
                scaleID: item.axis,
                value: value,
                borderDash: item.borderDash || [5, 10],
                borderColor: item.color || 'rgba(0, 0, 0, .4)',
                borderWidth: item.borderWidth || 1,
                label: {
                    enabled: !!item.label,
                    content: item.label,
                    fontStyle: 'normal',
                    backgroundColor: 'rgba(0, 0, 0, .8)',
                    fontSize: 12,
                    cornerRadius: 3,
                    position: 'right',
                    yPadding: 3,
                },
            };
        });
    }

    clickLegend(item: LegendItem, index: number) {
        const hidden = !item.hidden;
        this.chart.data.datasets[index].hidden = hidden;
        if (this.datasets()?.[index]) {
            this.datasets()[index].hidden = hidden;
            this.onDatasetHidden.emit({
                dataset: this.datasets()[index],
                hidden: hidden,
            });
        }
        this.chart.update();
    }
}
